import { inject, Injectable } from '@angular/core';
import { DateService } from '@core/services/date.service';
import { BasketItem, CombinedBasketItem } from '../basket';

/**
 * Service for combining and filtering basket timeslots.
 * Handles logic for merging consecutive periods and removing overlaps.
 */
@Injectable({
  providedIn: 'root',
})
export class BasketCombinerService {
  private readonly dateService = inject(DateService);

  /**
   * Combines consecutive or identical timeslots for the same resource into grouped periods.
   * Also removes overlapping periods, keeping the longest ones.
   *
   * Algorithm:
   * 1. Sort by resource ID, then by start time
   * 2. Remove fully overlapped periods (keeps longest)
   * 3. Combine consecutive/touching periods into CombinedBasketItems
   *
   * @param basketItems - Array of individual basket items to combine
   * @returns Array of combined basket items with count and combinedTimeslotIds
   */
  combine(basketItems: BasketItem[]): CombinedBasketItem[] {
    if (!basketItems?.length) return [];

    // Sort by resource, then by start time — timestamps pre-calculated once, not per comparison
    const sorted = basketItems
      .map((item) => ({ item, start: this.dateService.fromApiString(item.start).getTime() }))
      .sort((a, b) => (a.item.resourceId !== b.item.resourceId ? a.item.resourceId - b.item.resourceId : a.start - b.start))
      .map(({ item }) => item);

    // Step 1: Remove overlapping periods, keeping the longest ones
    const { filteredItems, removedItemsMap } = this.removeOverlappedPeriods(sorted);

    // Step 2: Combine consecutive or identical timeslots
    const combined: CombinedBasketItem[] = [];
    let current: CombinedBasketItem | null = null;

    for (const item of filteredItems) {
      const allIds = [item.id, ...(removedItemsMap.get(item) || [])];

      if (!current) {
        current = { ...item, count: 1, combinedTimeslotIds: allIds, price: item.price ?? 0 };
        continue;
      }

      // Pre-calculate timestamps once
      const currentStart = this.dateService.fromApiString(current.start).getTime();
      const currentEnd = this.dateService.fromApiString(current.end).getTime();
      const itemStart = this.dateService.fromApiString(item.start).getTime();
      const itemEnd = this.dateService.fromApiString(item.end).getTime();

      // Check if items can be combined (same resource + touching/overlapping periods)
      const isSameResource = current.resourceId === item.resourceId;
      const isConnected = isSameResource && currentEnd >= itemStart && currentStart <= itemEnd;

      if (isConnected) {
        // Extend period if item goes beyond current end (handles both consecutive and identical)
        if (itemEnd > currentEnd) {
          current.end = item.end;
        }
        current.count += 1;
        current.combinedTimeslotIds.push(...allIds);
        current.price += item.price ?? 0;
      } else {
        // Start new group
        combined.push(current);
        current = { ...item, count: 1, combinedTimeslotIds: allIds, price: item.price ?? 0 };
      }
    }

    if (current) combined.push(current);
    return combined;
  }

  /**
   * Removes timeslots that are fully covered by another longer timeslot.
   * When multiple periods overlap, keeps the longest one.
   *
   * @param items - Sorted array of basket items
   * @returns Object with filtered items and a map of removed IDs per kept item
   */
  private removeOverlappedPeriods(items: BasketItem[]): {
    filteredItems: BasketItem[];
    removedItemsMap: Map<BasketItem, string[]>;
  } {
    if (items.length <= 1) return { filteredItems: items, removedItemsMap: new Map() };

    // Pre-calculate timestamps to avoid repeated Date conversions
    const itemsWithTimestamps = items.map((item) => ({
      item,
      start: this.dateService.fromApiString(item.start).getTime(),
      end: this.dateService.fromApiString(item.end).getTime(),
    }));

    // Group by resource - only compare items for the same resource
    const resourceGroups = new Map<number, typeof itemsWithTimestamps>();
    for (const entry of itemsWithTimestamps) {
      const resourceId = entry.item.resourceId;
      if (!resourceGroups.has(resourceId)) {
        resourceGroups.set(resourceId, []);
      }
      resourceGroups.get(resourceId)!.push(entry);
    }

    const toKeep = new Set<BasketItem>(items);
    const removedItemsMap = new Map<BasketItem, string[]>();

    // Track which IDs each kept item replaced
    const addRemovedId = (keeper: BasketItem, removed: BasketItem) => {
      if (!removedItemsMap.has(keeper)) {
        removedItemsMap.set(keeper, []);
      }
      removedItemsMap.get(keeper)!.push(removed.id);
    };

    // Check each resource group for overlaps
    for (const resourceItems of resourceGroups.values()) {
      for (let i = 0; i < resourceItems.length; i++) {
        const entryA = resourceItems[i];
        if (!toKeep.has(entryA.item)) continue;

        const durationA = entryA.end - entryA.start;

        for (let j = i + 1; j < resourceItems.length; j++) {
          const entryB = resourceItems[j];
          if (!toKeep.has(entryB.item)) continue;

          const durationB = entryB.end - entryB.start;

          // Check if one fully covers the other
          const aCoversB = entryA.start <= entryB.start && entryA.end >= entryB.end;
          const bCoversA = entryB.start <= entryA.start && entryB.end >= entryA.end;

          if (aCoversB && durationA >= durationB) {
            // itemA fully covers itemB and is longer or equal - keep A, remove B
            toKeep.delete(entryB.item);
            addRemovedId(entryA.item, entryB.item);
          } else if (bCoversA && durationB > durationA) {
            // itemB fully covers itemA and is longer - keep B, remove A
            toKeep.delete(entryA.item);
            addRemovedId(entryB.item, entryA.item);
            break; // itemA is removed, no need to continue with it
          }
        }
      }
    }

    return { filteredItems: items.filter((item) => toKeep.has(item)), removedItemsMap };
  }
}
